import React, { useState, useEffect } from 'react';
import {
  X,
  Edit3,
  Plus,
  Trash2,
  CheckCircle2,
  Clock,
  UserCheck,
  UserX,
  AlertCircle,
  FileText,
  Save,
  Check,
  Users,
  Calendar,
  Layers
} from 'lucide-react';
import {
  Meeting,
  MeetingParticipant,
  MeetingAgenda,
  MeetingAgendaItem,
  MeetingMinutes,
  MeetingDecision,
  MeetingActionBridge,
  MeetingStatusHistory,
  MeetingVersion
} from './types';
import { meetingsService } from './meetingsService';

interface Props {
  meeting: Meeting;
  onClose: () => void;
  onRefresh: () => void;
}

type TabType = 'overview' | 'participants' | 'agenda' | 'minutes' | 'decisions' | 'actions' | 'history';

export const MeetingDetailModal: React.FC<Props> = ({ meeting, onClose, onRefresh }) => {
  const [currentMeeting, setCurrentMeeting] = useState<Meeting>(meeting);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [agenda, setAgenda] = useState<MeetingAgenda | undefined>(undefined);
  const [agendaItems, setAgendaItems] = useState<MeetingAgendaItem[]>([]);
  const [minutes, setMinutes] = useState<MeetingMinutes | null>(null);
  const [decisions, setDecisions] = useState<MeetingDecision[]>([]);
  const [actions, setActions] = useState<MeetingActionBridge[]>([]);
  const [statusHistory, setStatusHistory] = useState<MeetingStatusHistory[]>([]);
  const [versions, setVersions] = useState<MeetingVersion[]>([]);
  const [systemUsers, setSystemUsers] = useState<any[]>([]);

  // Overview Editing State
  const [isEditingOverview, setIsEditingOverview] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<string>(meeting.title);
  const [editDescription, setEditDescription] = useState<string>(meeting.description || '');
  const [editPlannedStart, setEditPlannedStart] = useState<string>(meeting.planned_start_time || '');
  const [editPlannedEnd, setEditPlannedEnd] = useState<string>(meeting.planned_end_time || '');
  const [editLocation, setEditLocation] = useState<string>(meeting.location || '');
  const [editTargetDepartment, setEditTargetDepartment] = useState<string>(meeting.target_department || 'Safety & Quality');
  const [editConfidentiality, setEditConfidentiality] = useState<string>(meeting.confidentiality_level || 'INTERNAL');
  const [editChairId, setEditChairId] = useState<string>(meeting.chair_id || '');
  const [editChairName, setEditChairName] = useState<string>(meeting.chair_name || '');
  const [editSecretaryName, setEditSecretaryName] = useState<string>(meeting.secretary_name || '');
  const [editIsVirtual, setEditIsVirtual] = useState<boolean>(Boolean(meeting.is_virtual));
  const [editVideoUrl, setEditVideoUrl] = useState<string>(meeting.video_conference_url || '');

  // State transitions
  const [transitionStatus, setTransitionStatus] = useState<string>('IN_PROGRESS');
  const [transitionReason, setTransitionReason] = useState<string>('');

  // Participant Form
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [participantRole, setParticipantRole] = useState<string>('ATTENDEE');
  const [participantIsRequired, setParticipantIsRequired] = useState<boolean>(true);

  // Agenda Item Form
  const [newAgendaTitle, setNewAgendaTitle] = useState<string>('');
  const [newAgendaDescription, setNewAgendaDescription] = useState<string>('');
  const [newAgendaPresenterId, setNewAgendaPresenterId] = useState<string>('');
  const [newAgendaDuration, setNewAgendaDuration] = useState<number>(20);
  const [newAgendaIsDiscussion, setNewAgendaIsDiscussion] = useState<boolean>(true);
  const [newAgendaIsDecision, setNewAgendaIsDecision] = useState<boolean>(false);
  const [newAgendaIsAction, setNewAgendaIsAction] = useState<boolean>(false);

  // Minutes Form
  const [minutesTitle, setMinutesTitle] = useState<string>(`${meeting.title} - Official Minutes`);
  const [minutesSummary, setMinutesSummary] = useState<string>('');
  const [preparedByName, setPreparedByName] = useState<string>('Recording Secretary');

  // Decision Form
  const [decisionTitle, setDecisionTitle] = useState<string>('');
  const [decisionText, setDecisionText] = useState<string>('');
  const [decisionType, setDecisionType] = useState<string>('OPERATIONAL');

  // Action Bridge Form
  const [actionTitle, setActionTitle] = useState<string>('');
  const [actionDescription, setActionDescription] = useState<string>('');
  const [actionAssigneeUserId, setActionAssigneeUserId] = useState<string>('');
  const [actionPriority, setActionPriority] = useState<string>('MEDIUM');
  const [actionType, setActionType] = useState<string>('CORRECTIVE');
  const [actionDueDate, setActionDueDate] = useState<string>(new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]);

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load system users on mount
  useEffect(() => {
    meetingsService.getSystemUsers().then((users) => {
      setSystemUsers(users || []);
      if (users && users.length > 0) {
        setSelectedUserId(users[0].id);
        setActionAssigneeUserId(users[0].id);
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    setCurrentMeeting(meeting);
    setEditTitle(meeting.title);
    setEditDescription(meeting.description || '');
    setEditPlannedStart(meeting.planned_start_time || '');
    setEditPlannedEnd(meeting.planned_end_time || '');
    setEditLocation(meeting.location || '');
    setEditTargetDepartment(meeting.target_department || 'Safety & Quality');
    setEditConfidentiality(meeting.confidentiality_level || 'INTERNAL');
    setEditChairId(meeting.chair_id || '');
    setEditChairName(meeting.chair_name || '');
    setEditSecretaryName(meeting.secretary_name || '');
    setEditIsVirtual(Boolean(meeting.is_virtual));
    setEditVideoUrl(meeting.video_conference_url || '');
  }, [meeting]);

  const loadTabData = async () => {
    try {
      if (activeTab === 'participants') {
        const p = await meetingsService.getParticipants(currentMeeting.id);
        setParticipants(p);
      } else if (activeTab === 'agenda') {
        const ag = await meetingsService.getAgenda(currentMeeting.id);
        setAgenda(ag.agenda);
        setAgendaItems(ag.items || []);
      } else if (activeTab === 'minutes') {
        const m = await meetingsService.getMinutes(currentMeeting.id);
        setMinutes(m);
      } else if (activeTab === 'decisions') {
        const d = await meetingsService.getDecisions(currentMeeting.id);
        setDecisions(d);
      } else if (activeTab === 'actions') {
        const a = await meetingsService.getLinkedActions(currentMeeting.id);
        setActions(a);
      } else if (activeTab === 'history') {
        const sh = await meetingsService.getStatusHistory(currentMeeting.id);
        const v = await meetingsService.getVersions(currentMeeting.id);
        setStatusHistory(sh);
        setVersions(v);
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadTabData();
  }, [activeTab, currentMeeting.id]);

  // Handle Save Overview Details
  const handleSaveOverview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage(null);

      const chairUser = systemUsers.find(u => u.id === editChairId);
      const chairDisplayName = chairUser ? chairUser.display_name : editChairName;

      const updated = await meetingsService.updateMeeting(currentMeeting.id, {
        title: editTitle,
        description: editDescription,
        planned_start_time: editPlannedStart,
        planned_end_time: editPlannedEnd,
        location: editLocation,
        target_department: editTargetDepartment,
        confidentiality_level: editConfidentiality,
        chair_id: editChairId,
        chair_name: chairDisplayName,
        secretary_name: editSecretaryName,
        is_virtual: editIsVirtual,
        video_conference_url: editVideoUrl
      });

      setCurrentMeeting(updated);
      setIsEditingOverview(false);
      setMessage({ type: 'success', text: 'Toplantı detayları başarıyla güncellendi.' });
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Güncelleme sırasında hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusTransition = async () => {
    try {
      setLoading(true);
      setMessage(null);
      const updated = await meetingsService.transitionStatus(currentMeeting.id, transitionStatus, transitionReason || 'Status transitioned');
      setCurrentMeeting(updated);
      setMessage({ type: 'success', text: `Meeting status updated to '${transitionStatus}'.` });
      setTransitionReason('');
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Participant Operations
  const handleAddParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;
    try {
      setLoading(true);
      setMessage(null);
      const user = systemUsers.find(u => u.id === selectedUserId);
      const userName = user ? user.display_name : selectedUserId;
      const userEmail = user?.email || `${selectedUserId}@luminixaviation.com`;

      await meetingsService.addParticipant(currentMeeting.id, {
        user_id: selectedUserId,
        user_name: userName,
        email: userEmail,
        participant_role: participantRole as any,
        is_required: participantIsRequired ? 1 : 0
      });

      setMessage({ type: 'success', text: `${userName} toplantı katılımcı listesine eklendi.` });
      loadTabData();
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteParticipant = async (participantId: string, name: string) => {
    if (!window.confirm(`${name} isimli katılımcıyı listeden çıkarmak istediğinize emin misiniz?`)) return;
    try {
      setLoading(true);
      await meetingsService.deleteParticipant(currentMeeting.id, participantId);
      setMessage({ type: 'success', text: `${name} katılımcı listesinden çıkarıldı.` });
      loadTabData();
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAttendance = async (pId: string, status: string) => {
    try {
      setLoading(true);
      await meetingsService.updateAttendance(currentMeeting.id, pId, status);
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Agenda Operations
  const handleAddAgendaItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAgendaTitle.trim()) return;
    try {
      setLoading(true);
      setMessage(null);
      const presenterUser = systemUsers.find(u => u.id === newAgendaPresenterId);
      const presenterName = presenterUser ? presenterUser.display_name : undefined;

      await meetingsService.addAgendaItem(currentMeeting.id, {
        title: newAgendaTitle.trim(),
        description: newAgendaDescription.trim() || undefined,
        presenter_id: newAgendaPresenterId || undefined,
        presenter_name: presenterName,
        duration_minutes: Number(newAgendaDuration),
        is_discussion: newAgendaIsDiscussion ? 1 : 0,
        is_decision_required: newAgendaIsDecision ? 1 : 0,
        is_action_required: newAgendaIsAction ? 1 : 0,
        item_status: 'PENDING'
      });

      setNewAgendaTitle('');
      setNewAgendaDescription('');
      setNewAgendaPresenterId('');
      setNewAgendaDuration(20);
      setNewAgendaIsDiscussion(true);
      setNewAgendaIsDecision(false);
      setNewAgendaIsAction(false);
      setMessage({ type: 'success', text: 'Yeni gündem maddesi başarıyla eklendi.' });
      loadTabData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgendaStatus = async (itemId: string, newStatus: string) => {
    try {
      await meetingsService.updateAgendaItem(currentMeeting.id, itemId, { item_status: newStatus as any });
      loadTabData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeleteAgendaItem = async (itemId: string) => {
    if (!window.confirm('Bu gündem maddesini silmek istediğinize emin misiniz?')) return;
    try {
      setLoading(true);
      await meetingsService.deleteAgendaItem(currentMeeting.id, itemId);
      loadTabData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Minutes & Decisions Operations
  const handleCreateMinutes = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await meetingsService.createMinutes(currentMeeting.id, {
        title: minutesTitle,
        general_summary: minutesSummary,
        prepared_by_id: 'USR-SEC-01',
        prepared_by_name: preparedByName
      });
      setMessage({ type: 'success', text: 'Toplantı tutanağı taslağı başarıyla oluşturuldu.' });
      loadTabData();
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMinutes = async () => {
    try {
      setLoading(true);
      await meetingsService.approveMinutes(currentMeeting.id, 'USR-AM-01', currentMeeting.chair_name, 'Meeting Chair formal approval completed');
      setMessage({ type: 'success', text: 'Toplantı tutanağı SoD kontrolüyle onaylandı.' });
      loadTabData();
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRecordDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!decisionTitle.trim()) return;
    try {
      setLoading(true);
      await meetingsService.recordDecision(currentMeeting.id, {
        title: decisionTitle,
        decision_text: decisionText,
        decision_type: decisionType as any,
        decision_maker_name: currentMeeting.chair_name
      });
      setDecisionTitle('');
      setDecisionText('');
      setMessage({ type: 'success', text: 'Resmi komite kararı başarıyla kaydedildi.' });
      loadTabData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Action Bridge Operations
  const handleLinkAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionTitle.trim() || !actionAssigneeUserId) return;
    try {
      setLoading(true);
      setMessage(null);
      const assigneeUser = systemUsers.find(u => u.id === actionAssigneeUserId);
      const assigneeName = assigneeUser ? assigneeUser.display_name : actionAssigneeUserId;
      const actionCode = `ACT-MTG-${Date.now().toString().slice(-4)}`;

      await meetingsService.linkCentralAction(currentMeeting.id, {
        central_action_id: `CENTRAL-${actionCode}`,
        action_code: actionCode,
        title: actionTitle.trim(),
        description: actionDescription.trim() || `Action from meeting ${currentMeeting.meeting_number}: ${actionTitle.trim()}`,
        priority: actionPriority,
        action_type: actionType,
        assigned_to_id: actionAssigneeUserId,
        assigned_to_name: assigneeName,
        due_date: actionDueDate
      });

      setActionTitle('');
      setActionDescription('');
      setMessage({
        type: 'success',
        text: `Eylem oluşturuldu ve ${assigneeName} kullanıcısına atandı. Görev, kullanıcının kişisel hesabında ve Merkezi Görev/Eylemler sayfasında listelenmektedir.`
      });
      loadTabData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateActionStatus = async (actId: string, status: string) => {
    try {
      await meetingsService.updateActionStatus(currentMeeting.id, actId, status);
      loadTabData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCloseMeeting = async () => {
    try {
      setLoading(true);
      await meetingsService.closeMeeting(currentMeeting.id, 'Accountable Meeting Lead');
      setMessage({ type: 'success', text: 'Meeting formally closed (CLOSED).' });
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                {currentMeeting.meeting_number}
              </span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500 font-mono">v{currentMeeting.version}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">
                {currentMeeting.status}
              </span>
              {currentMeeting.quorum_required ? (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                  currentMeeting.quorum_achieved ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {currentMeeting.quorum_achieved ? 'QUORUM ACHIEVED' : 'QUORUM PENDING'}
                </span>
              ) : null}
            </div>
            <h3>{currentMeeting.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 px-6 gap-2 bg-gray-50 overflow-x-auto text-xs font-medium">
          {[
            { id: 'overview', label: 'Overview & Details' },
            { id: 'participants', label: 'Participants & Roll Call' },
            { id: 'agenda', label: 'Agenda Items' },
            { id: 'minutes', label: 'Meeting Minutes' },
            { id: 'decisions', label: 'Decisions Log' },
            { id: 'actions', label: 'Action Items' },
            { id: 'history', label: 'Versions & Audit' }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`py-3 px-3 border-b-2 transition ${
                activeTab === tab.id ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {message && (
            <div className={`p-3 rounded-lg text-xs border flex items-center justify-between ${
              message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'
            }`}>
              <span>{message.text}</span>
              <button type="button" onClick={() => setMessage(null)} className="text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            </div>
          )}

          {/* 1. OVERVIEW & DETAILS TAB */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                {isEditingOverview ? (
                  /* Edit Meeting Details Form */
                  <form onSubmit={handleSaveOverview} className="bg-white p-5 rounded-xl border border-blue-200 shadow-sm space-y-4 text-xs">
                    <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                      <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                        <Edit3 size={16} className="text-blue-600" />
                        Toplantı Detaylarını Düzenle
                      </h4>
                      <span className="text-[11px] text-gray-500">v{currentMeeting.version} Snapshot oluşturulacaktır</span>
                    </div>

                    <div className="form-field">
                      <label className="font-semibold text-gray-700">Toplantı Başlığı *</label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        required
                        className="w-full p-2 border border-gray-300 rounded-lg text-xs font-semibold"
                      />
                    </div>

                    <div className="form-field">
                      <label className="font-semibold text-gray-700">Açıklama / Toplantı Kapsamı</label>
                      <textarea
                        rows={3}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Toplantı amacı ve kapsam detayları..."
                        className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Planlanan Başlangıç Tarih/Saat *</label>
                        <input
                          type="text"
                          value={editPlannedStart}
                          onChange={(e) => setEditPlannedStart(e.target.value)}
                          placeholder="YYYY-MM-DD HH:MM:SS"
                          required
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
                        />
                      </div>
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Planlanan Bitiş Tarih/Saat *</label>
                        <input
                          type="text"
                          value={editPlannedEnd}
                          onChange={(e) => setEditPlannedEnd(e.target.value)}
                          placeholder="YYYY-MM-DD HH:MM:SS"
                          required
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Toplantı Yeri / Salon</label>
                        <input
                          type="text"
                          value={editLocation}
                          onChange={(e) => setEditLocation(e.target.value)}
                          placeholder="Örn: HQ Main Executive Boardroom"
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">İlgili Departman</label>
                        <input
                          type="text"
                          value={editTargetDepartment}
                          onChange={(e) => setEditTargetDepartment(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Toplantı Başkanı (Chair)</label>
                        <select
                          value={editChairId}
                          onChange={(e) => {
                            setEditChairId(e.target.value);
                            const u = systemUsers.find(usr => usr.id === e.target.value);
                            if (u) setEditChairName(u.display_name);
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                        >
                          <option value="">Seçiniz veya mevcut başkanı kullanın</option>
                          {systemUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.display_name} ({u.role})</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Sekreter / Raportör</label>
                        <input
                          type="text"
                          value={editSecretaryName}
                          onChange={(e) => setEditSecretaryName(e.target.value)}
                          placeholder="Örn: Selin Öztürk"
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Gizlilik Düzeyi</label>
                        <select
                          value={editConfidentiality}
                          onChange={(e) => setEditConfidentiality(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                        >
                          <option value="INTERNAL">INTERNAL</option>
                          <option value="RESTRICTED">RESTRICTED</option>
                          <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                          <option value="PUBLIC">PUBLIC</option>
                        </select>
                      </div>
                      <div className="form-field flex flex-col justify-end">
                        <label className="flex items-center gap-2 cursor-pointer pb-2">
                          <input
                            type="checkbox"
                            checked={editIsVirtual}
                            onChange={(e) => setEditIsVirtual(e.target.checked)}
                            className="rounded text-blue-600"
                          />
                          <span className="font-medium text-gray-700">Sanal / Çevrimiçi Toplantı (Online)</span>
                        </label>
                      </div>
                    </div>

                    {editIsVirtual && (
                      <div className="form-field">
                        <label className="font-semibold text-gray-700">Video Konferans Bağlantısı</label>
                        <input
                          type="url"
                          value={editVideoUrl}
                          onChange={(e) => setEditVideoUrl(e.target.value)}
                          placeholder="https://meet.luminixaviation.com/..."
                          className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setIsEditingOverview(false)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-xs font-medium"
                      >
                        İptal
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                      >
                        <Save size={14} />
                        {loading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                      </button>
                    </div>
                  </form>
                ) : (
                  /* Read-only View */
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 text-xs space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-gray-500 font-semibold uppercase block mb-1">Açıklama / Kapsam</span>
                        <p className="text-gray-800 text-sm leading-relaxed">{currentMeeting.description || 'Açıklama girilmemiş.'}</p>
                      </div>
                      {!currentMeeting.is_closed && (
                        <button
                          type="button"
                          onClick={() => setIsEditingOverview(true)}
                          className="px-3 py-1.5 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 text-gray-700 rounded-lg text-xs font-semibold shadow-sm flex items-center gap-1.5 transition"
                        >
                          <Edit3 size={14} />
                          Detayları Düzenle
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                      <div><span className="text-gray-500">Departman:</span> <strong className="text-gray-900 ml-1">{currentMeeting.target_department}</strong></div>
                      <div><span className="text-gray-500">Gizlilik:</span> <strong className="text-gray-900 ml-1">{currentMeeting.confidentiality_level}</strong></div>
                      <div><span className="text-gray-500">Planlanan Başlangıç:</span> <strong className="text-gray-900 ml-1 font-mono">{currentMeeting.planned_start_time}</strong></div>
                      <div><span className="text-gray-500">Planlanan Bitiş:</span> <strong className="text-gray-900 ml-1 font-mono">{currentMeeting.planned_end_time}</strong></div>
                      <div><span className="text-gray-500">Konum / Salon:</span> <strong className="text-gray-900 ml-1">{currentMeeting.location}</strong></div>
                      <div><span className="text-gray-500">Toplantı Başkanı:</span> <strong className="text-gray-900 ml-1">{currentMeeting.chair_name}</strong></div>
                      <div><span className="text-gray-500">Organizatör:</span> <strong className="text-gray-900 ml-1">{currentMeeting.organizer_name}</strong></div>
                      <div><span className="text-gray-500">Yönetim Gözden Geçirme:</span> <strong className="text-gray-900 ml-1">{currentMeeting.is_management_review ? 'Evet (Management Review)' : 'Hayır'}</strong></div>
                      {currentMeeting.is_virtual && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Online Link:</span>
                          <a href={currentMeeting.video_conference_url || '#'} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline ml-1 font-mono">
                            {currentMeeting.video_conference_url || 'Belirtilmemiş'}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {currentMeeting.is_closed ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                    <span>Bu toplantı resmi olarak kapatılmıştır. (Kapatan: {currentMeeting.closed_by || 'Yetkili'}, Tarih: {currentMeeting.closed_at})</span>
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={handleCloseMeeting}
                      disabled={loading}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition"
                    >
                      Toplantıyı Resmi Olarak Kapat (Formally Close)
                    </button>
                  </div>
                )}
              </div>

              <div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Durum Değişimi (Status Transition)</h4>
                  <div>
                    <span className="text-xs text-gray-500 block">Mevcut Durum:</span>
                    <span className="font-bold text-blue-700 text-sm">{currentMeeting.status}</span>
                  </div>

                  {!currentMeeting.is_closed && (
                    <>
                      <div className="form-field">
                        <label>Hedef Durum</label>
                        <select
                          value={transitionStatus}
                          onChange={(e) => setTransitionStatus(e.target.value)}
                        >
                          <option value="SCHEDULED">SCHEDULED</option>
                          <option value="INVITATIONS_SENT">INVITATIONS SENT</option>
                          <option value="PREPARATION">PREPARATION</option>
                          <option value="IN_PROGRESS">IN PROGRESS</option>
                          <option value="COMPLETED">COMPLETED</option>
                          <option value="FOLLOW_UP">FOLLOW UP</option>
                          <option value="CANCELLED">CANCELLED</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Gerekçe / Not</label>
                        <input
                          type="text"
                          value={transitionReason}
                          onChange={(e) => setTransitionReason(e.target.value)}
                          placeholder="Durum güncelleme notu..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleStatusTransition}
                        disabled={loading}
                        className="btn-primary w-full"
                      >
                        {loading ? 'Güncelleniyor...' : 'Durumu Güncelle'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 2. PARTICIPANTS TAB */}
          {activeTab === 'participants' && (
            <div className="space-y-5">
              {/* Add Participant Form */}
              <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Users size={15} className="text-blue-600" />
                  Katılımcı Ekle (Select & Add Participant)
                </h4>
                <form onSubmit={handleAddParticipant} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-5 form-field mb-0">
                    <label className="text-[11px] font-semibold text-gray-700">Kullanıcı Seçimi *</label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      required
                    >
                      {systemUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.display_name} ({u.role}) — {u.department || 'Operations'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-3 form-field mb-0">
                    <label className="text-[11px] font-semibold text-gray-700">Toplantı Rolü</label>
                    <select
                      value={participantRole}
                      onChange={(e) => setParticipantRole(e.target.value)}
                      className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="ATTENDEE">ATTENDEE (Katılımcı)</option>
                      <option value="CHAIR">CHAIR (Başkan)</option>
                      <option value="SECRETARY">SECRETARY (Raportör)</option>
                      <option value="PRESENTER">PRESENTER (Sunucu)</option>
                      <option value="OBSERVER">OBSERVER (Gözlemci)</option>
                      <option value="GUEST">GUEST (Misafir)</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 form-field mb-0 flex items-center pb-2">
                    <label className="flex items-center gap-2 cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        checked={participantIsRequired}
                        onChange={(e) => setParticipantIsRequired(e.target.checked)}
                        className="rounded text-blue-600"
                      />
                      <span className="text-gray-700 font-medium">Zorunlu</span>
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={loading || !selectedUserId}
                      className="btn-primary w-full text-xs flex items-center justify-center gap-1.5 py-2"
                    >
                      <Plus size={14} />
                      Katılımcı Ekle
                    </button>
                  </div>
                </form>
              </div>

              {/* Participants List */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900">Kayıtlı Katılımcı Listesi ve Yoklama</h3>
                  <span className="text-xs text-gray-500 font-mono">Toplam: {participants.length} kişi</span>
                </div>

                {participants.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-xs bg-gray-50 rounded-xl border border-gray-200">
                    Henüz kayıtlı katılımcı bulunmamaktadır. Yukarıdaki formdan katılımcı ekleyebilirsiniz.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200 text-left text-gray-600 font-semibold">
                        <tr>
                          <th className="p-3">Kullanıcı</th>
                          <th className="p-3">Rol</th>
                          <th className="p-3">Zorunlu</th>
                          <th className="p-3">Davet Durumu</th>
                          <th className="p-3">Yoklama Durumu</th>
                          <th className="p-3 text-right">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {participants.map((p) => (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="p-3">
                              <div className="font-semibold text-gray-900">{p.user_name}</div>
                              {p.email && <div className="text-gray-500 text-[11px] font-mono">{p.email}</div>}
                            </td>
                            <td className="p-3">
                              <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-800">
                                {p.participant_role}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                p.is_required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {p.is_required ? 'Zorunlu' : 'İsteğe Bağlı'}
                              </span>
                            </td>
                            <td className="p-3 text-gray-600 font-medium">{p.invitation_status}</td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                p.attendance_status === 'PRESENT' ? 'bg-emerald-100 text-emerald-800' :
                                p.attendance_status === 'EXCUSED_ABSENCE' ? 'bg-amber-100 text-amber-800' :
                                p.attendance_status === 'UNEXCUSED_ABSENCE' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {p.attendance_status}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex justify-end gap-1.5 items-center">
                                <button
                                  type="button"
                                  onClick={() => handleAttendance(p.id, 'PRESENT')}
                                  title="Katıldı (Present)"
                                  className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-semibold"
                                >
                                  Katıldı
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAttendance(p.id, 'EXCUSED_ABSENCE')}
                                  title="Mazeretli (Excused)"
                                  className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[10px] font-semibold"
                                >
                                  Mazeretli
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAttendance(p.id, 'UNEXCUSED_ABSENCE')}
                                  title="Katılmadı (Absent)"
                                  className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-[10px] font-semibold"
                                >
                                  Katılmadı
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteParticipant(p.id, p.user_name)}
                                  title="Katılımcıyı Listeden Çıkar"
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded ml-1 transition"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 3. AGENDA TAB */}
          {activeTab === 'agenda' && (
            <div className="space-y-5">
              {/* Add Agenda Item Form */}
              <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Calendar size={15} className="text-blue-600" />
                  Yeni Gündem Maddesi Ekle (Add Agenda Item & Details)
                </h4>
                <form onSubmit={handleAddAgendaItem} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-8 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Gündem Maddesi Başlığı *</label>
                      <input
                        type="text"
                        placeholder="Örn: 2026 Q2 Kuş Çarpması ve Pist Olay Analizleri Değerlendirmesi"
                        value={newAgendaTitle}
                        onChange={(e) => setNewAgendaTitle(e.target.value)}
                        required
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-4 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Tahmini Süre (Dakika)</label>
                      <input
                        type="number"
                        min={5}
                        max={180}
                        value={newAgendaDuration}
                        onChange={(e) => setNewAgendaDuration(Number(e.target.value))}
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg font-mono"
                      />
                    </div>
                  </div>

                  <div className="form-field mb-0">
                    <label className="text-[11px] font-semibold text-gray-700">Gündem Maddesi Açıklaması & Görüşülecek Konular</label>
                    <textarea
                      rows={2}
                      placeholder="Maddenin kapsamı, incelenecek raporlar ve tartışılacak başlıklar..."
                      value={newAgendaDescription}
                      onChange={(e) => setNewAgendaDescription(e.target.value)}
                      className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-6 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Sunucu / Raportör (Presenter)</label>
                      <select
                        value={newAgendaPresenterId}
                        onChange={(e) => setNewAgendaPresenterId(e.target.value)}
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Belirtilmemiş / Genel Tartışma</option>
                        {systemUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.display_name} ({u.role})</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-4 flex items-center gap-3 pb-2 text-xs">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAgendaIsDiscussion}
                          onChange={(e) => setNewAgendaIsDiscussion(e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-700 font-medium">Tartışma</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAgendaIsDecision}
                          onChange={(e) => setNewAgendaIsDecision(e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-700 font-medium">Karar</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAgendaIsAction}
                          onChange={(e) => setNewAgendaIsAction(e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-700 font-medium">Eylem</span>
                      </label>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={loading || !newAgendaTitle.trim()}
                        className="btn-primary w-full text-xs flex items-center justify-center gap-1.5 py-2"
                      >
                        <Plus size={14} />
                        Maddeyi Ekle
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Agenda Items List */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900">Gündem Maddeleri Sıralaması</h3>
                  <span className="text-xs text-gray-500 font-mono">Toplam: {agendaItems.length} madde</span>
                </div>

                {agendaItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-xs bg-gray-50 rounded-xl border border-gray-200">
                    Henüz kayıtlı gündem maddesi bulunmamaktadır. Yukarıdaki formdan yeni madde ekleyebilirsiniz.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {agendaItems.map((item) => (
                      <div key={item.id} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition text-xs space-y-2">
                        <div className="flex justify-between items-start gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-blue-700 px-2 py-0.5 rounded bg-blue-50 text-[11px]">
                                {item.item_code}
                              </span>
                              <span className="font-bold text-gray-900 text-sm">{item.title}</span>
                            </div>
                            <div className="text-gray-500 text-[11px] flex items-center gap-3">
                              <span className="flex items-center gap-1">
                                <Clock size={12} className="text-gray-400" />
                                Estimated: {item.duration_minutes} min ({item.duration_minutes} dk)
                              </span>
                              {item.presenter_name && (
                                <span className="flex items-center gap-1">
                                  <UserCheck size={12} className="text-gray-400" />
                                  Sunum: {item.presenter_name}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Status Selector */}
                            <select
                              value={item.item_status}
                              onChange={(e) => handleUpdateAgendaStatus(item.id, e.target.value)}
                              className={`text-[10px] font-bold px-2 py-1 rounded border cursor-pointer ${
                                item.item_status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                                item.item_status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-800 border-blue-200' :
                                item.item_status === 'POSTPONED' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                                'bg-gray-50 text-gray-700 border-gray-200'
                              }`}
                            >
                              <option value="PENDING">PENDING</option>
                              <option value="IN_PROGRESS">IN PROGRESS</option>
                              <option value="COMPLETED">COMPLETED</option>
                              <option value="POSTPONED">POSTPONED</option>
                              <option value="SKIPPED">SKIPPED</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => handleDeleteAgendaItem(item.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded transition"
                              title="Gündem maddesini sil"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>

                        {item.description && (
                          <div className="p-2.5 bg-gray-50 rounded-lg text-gray-700 text-xs border border-gray-100 leading-relaxed">
                            {item.description}
                          </div>
                        )}

                        <div className="flex items-center gap-2 pt-1 text-[10px]">
                          {item.is_discussion ? <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-semibold">Tartışma</span> : null}
                          {item.is_decision_required ? <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">Karar Gerekir</span> : null}
                          {item.is_action_required ? <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">Eylem Gerekir</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 4. MINUTES TAB */}
          {activeTab === 'minutes' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Resmi Toplantı Tutanağı (Official Minutes)</h3>
              {minutes ? (
                <div className="p-5 bg-white rounded-xl border border-gray-200 space-y-4 text-xs shadow-sm">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <div>
                      <span className="font-mono text-xs text-blue-600 font-bold block">{minutes.minutes_number}</span>
                      <h4 className="font-bold text-sm text-gray-900">{minutes.title}</h4>
                    </div>
                    <span className={`px-2.5 py-1 rounded text-[11px] font-bold ${
                      minutes.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {minutes.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-gray-600">
                    <div><strong>Hazırlayan:</strong> {minutes.prepared_by_name}</div>
                    {minutes.approved_by_name && (
                      <div className="text-emerald-800">
                        <strong>Onaylayan:</strong> {minutes.approved_by_name} ({minutes.approved_at})
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <strong className="block mb-1.5 text-gray-800">Genel Tutanak Özeti & Görüşülen Konular:</strong>
                    <p className="text-gray-800 leading-relaxed whitespace-pre-line">{minutes.general_summary}</p>
                  </div>

                  {minutes.status !== 'APPROVED' && (
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={handleApproveMinutes}
                        disabled={loading}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition flex items-center gap-1.5"
                      >
                        <Check size={14} />
                        Approve Minutes as Meeting Chair (SoD Enforced)
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleCreateMinutes} className="bg-gray-50 p-5 rounded-xl border border-gray-200 space-y-3">
                  <div className="form-field">
                    <label className="font-semibold text-gray-700">Tutanak Belge Başlığı *</label>
                    <input
                      type="text"
                      value={minutesTitle}
                      onChange={(e) => setMinutesTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="font-semibold text-gray-700">Tutanak Özeti & Alınan Kararlar *</label>
                    <textarea
                      rows={5}
                      value={minutesSummary}
                      onChange={(e) => setMinutesSummary(e.target.value)}
                      placeholder="Toplantıda müzakere edilen ana konular, mutabakata varılan hususlar ve süreç onayları..."
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="font-semibold text-gray-700">Raportör / Sekreter Adı</label>
                    <input
                      type="text"
                      value={preparedByName}
                      onChange={(e) => setPreparedByName(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                    >
                      Tutanak Taslağını Kaydet
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* 5. DECISIONS TAB */}
          {activeTab === 'decisions' && (
            <div className="space-y-5">
              {/* Record Decision Form */}
              <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-blue-600" />
                  Yeni Komite Kararı Kaydet (Record Decision)
                </h4>
                <form onSubmit={handleRecordDecision} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-8 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Karar Başlığı *</label>
                      <input
                        type="text"
                        placeholder="Örn: Kış operasyonları yaklaşma prosedürlerinin revizyonu kararı"
                        value={decisionTitle}
                        onChange={(e) => setDecisionTitle(e.target.value)}
                        required
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-4 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Karar Türü</label>
                      <select
                        value={decisionType}
                        onChange={(e) => setDecisionType(e.target.value)}
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      >
                        <option value="OPERATIONAL">OPERATIONAL</option>
                        <option value="SAFETY">SAFETY</option>
                        <option value="STRATEGIC">STRATEGIC</option>
                        <option value="POLICY">POLICY</option>
                        <option value="EMERGENCY">EMERGENCY</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-field mb-0">
                    <label className="text-[11px] font-semibold text-gray-700">Karar Metni ve Operasyonel Yönerge *</label>
                    <textarea
                      rows={3}
                      placeholder="Alınan resmi kararın detaylı metni ve yürürlük hükümleri..."
                      value={decisionText}
                      onChange={(e) => setDecisionText(e.target.value)}
                      required
                      className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="flex justify-end pt-1">
                    <button
                      type="submit"
                      disabled={loading || !decisionTitle.trim()}
                      className="btn-primary text-xs flex items-center gap-1.5 py-2 px-4"
                    >
                      <Check size={14} />
                      Kararı Kaydet
                    </button>
                  </div>
                </form>
              </div>

              {/* Decisions List */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900">Alınan Resmi Komite Kararları</h3>
                  <span className="text-xs text-gray-500 font-mono">Toplam: {decisions.length} karar</span>
                </div>

                {decisions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-xs bg-gray-50 rounded-xl border border-gray-200">
                    Henüz kayıtlı komite kararı bulunmamaktadır.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {decisions.map((d) => (
                      <div key={d.id} className="p-4 bg-white rounded-xl border border-gray-200 text-xs space-y-1.5 shadow-sm">
                        <div className="flex justify-between items-center font-semibold">
                          <span className="text-gray-900 font-bold text-sm">
                            <span className="font-mono text-blue-700 mr-2">{d.decision_number}</span>
                            {d.title}
                          </span>
                          <span className="text-emerald-700 font-bold px-2 py-0.5 rounded bg-emerald-50 text-[10px]">
                            {d.decision_status}
                          </span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{d.decision_text}</p>
                        <div className="text-[11px] text-gray-500 pt-1 flex items-center gap-3 border-t border-gray-100">
                          <span><strong>Karar Verici:</strong> {d.decision_maker_name}</span>
                          <span>•</span>
                          <span><strong>Yürürlük Tarihi:</strong> {d.effective_date}</span>
                          <span>•</span>
                          <span><strong>Tür:</strong> {d.decision_type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 6. ACTION ITEMS TAB (Central Tasks & Actions Bridge) */}
          {activeTab === 'actions' && (
            <div className="space-y-5">
              {/* Informative Banner */}
              <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 text-xs flex items-start gap-2.5">
                <Layers size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="block font-semibold">Merkezi Görev & Eylem Entegrasyonu Aktif:</strong>
                  <span>
                    Burada oluşturulan tüm aksiyonlar ve görevler ilgili personelin hesabına doğrudan atanır. Kullanıcı kendi hesabında,
                    <strong> Dashboard</strong> üzerinde ve <strong>Merkezi Görev/Eylemler (Tasks & Actions)</strong> sayfasında bu görevi görebilir ve takip edebilir.
                  </span>
                </div>
              </div>

              {/* Add Action Form */}
              <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Plus size={15} className="text-blue-600" />
                  Yeni Eylem / Görev Oluştur ve Ata (Assign Action to User)
                </h4>
                <form onSubmit={handleLinkAction} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-8 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Aksiyon / Görev Başlığı *</label>
                      <input
                        type="text"
                        placeholder="Örn: Uçuş işletme kış operasyon bülteninin güncellenmesi ve yayınlanması"
                        value={actionTitle}
                        onChange={(e) => setActionTitle(e.target.value)}
                        required
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg font-semibold"
                      />
                    </div>
                    <div className="md:col-span-4 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Son Tamamlanma Tarihi (Due Date) *</label>
                      <input
                        type="date"
                        value={actionDueDate}
                        onChange={(e) => setActionDueDate(e.target.value)}
                        required
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg font-mono"
                      />
                    </div>
                  </div>

                  <div className="form-field mb-0">
                    <label className="text-[11px] font-semibold text-gray-700">Aksiyon Açıklaması & Görev Yönergesi</label>
                    <textarea
                      rows={2}
                      placeholder="Görev kapsamında yapılması gereken çalışmalar, teslim edilecek dokümanlar ve başarı kriteri..."
                      value={actionDescription}
                      onChange={(e) => setActionDescription(e.target.value)}
                      className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-5 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Atanan Kullanıcı (Assignee) *</label>
                      <select
                        value={actionAssigneeUserId}
                        onChange={(e) => setActionAssigneeUserId(e.target.value)}
                        required
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg font-medium"
                      >
                        {systemUsers.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.display_name} ({u.role}) — {u.department || 'Operations'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-3 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Öncelik (Priority)</label>
                      <select
                        value={actionPriority}
                        onChange={(e) => setActionPriority(e.target.value)}
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      >
                        <option value="LOW">LOW (Düşük)</option>
                        <option value="MEDIUM">MEDIUM (Orta)</option>
                        <option value="HIGH">HIGH (Yüksek)</option>
                        <option value="CRITICAL">CRITICAL (Kritik)</option>
                      </select>
                    </div>

                    <div className="md:col-span-2 form-field mb-0">
                      <label className="text-[11px] font-semibold text-gray-700">Aksiyon Türü</label>
                      <select
                        value={actionType}
                        onChange={(e) => setActionType(e.target.value)}
                        className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                      >
                        <option value="CORRECTIVE">CORRECTIVE</option>
                        <option value="PREVENTIVE">PREVENTIVE</option>
                        <option value="SAFETY_IMPROVEMENT">SAFETY IMPROVEMENT</option>
                        <option value="DIRECTIVE">DIRECTIVE</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={loading || !actionTitle.trim() || !actionAssigneeUserId}
                        className="btn-primary w-full text-xs flex items-center justify-center gap-1.5 py-2"
                      >
                        <Plus size={14} />
                        Aksiyonu Ata
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Actions List */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900">Toplantıya Bağlı Aksiyon ve Görevler</h3>
                  <span className="text-xs text-gray-500 font-mono">Toplam: {actions.length} eylem</span>
                </div>

                {actions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-xs bg-gray-50 rounded-xl border border-gray-200">
                    Henüz oluşturulmuş aksiyon bulunmamaktadır. Yukarıdaki formdan yeni eylem atayabilirsiniz.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {actions.map((a) => (
                      <div key={a.id} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm text-xs space-y-2">
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-indigo-700 px-2 py-0.5 rounded bg-indigo-50 text-[11px]">
                                {a.action_code}
                              </span>
                              <span className="font-bold text-gray-900 text-sm">{a.title}</span>
                              <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-gray-100 text-gray-700">
                                {a.action_type}
                              </span>
                            </div>
                            <div className="text-gray-500 text-[11px] mt-1 flex items-center gap-3">
                              <span><strong>Atanan:</strong> {a.assigned_to_name}</span>
                              <span>•</span>
                              <span><strong>Vade:</strong> {a.due_date}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Status Selector */}
                            <select
                              value={a.status}
                              onChange={(e) => handleUpdateActionStatus(a.id, e.target.value)}
                              className={`text-[10px] font-bold px-2 py-1 rounded border cursor-pointer ${
                                a.status === 'COMPLETED' || a.status === 'CLOSED' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                                a.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-800 border-blue-200' :
                                'bg-amber-50 text-amber-800 border-amber-200'
                              }`}
                            >
                              <option value="OPEN">OPEN</option>
                              <option value="IN_PROGRESS">IN PROGRESS</option>
                              <option value="COMPLETED">COMPLETED</option>
                              <option value="CLOSED">CLOSED</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 7. AUDIT HISTORY TAB */}
          {activeTab === 'history' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Status Transition Audit Trail</h4>
                <div className="space-y-2">
                  {statusHistory.length === 0 ? (
                    <div className="text-xs text-gray-400 p-3 bg-gray-50 rounded-lg">Kayıtlı durum geçmişi yok.</div>
                  ) : (
                    statusHistory.map((sh) => (
                      <div key={sh.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs">
                        <div className="font-semibold text-blue-700">{sh.from_status} → {sh.to_status}</div>
                        <div className="text-gray-600 mt-0.5">{sh.reason}</div>
                        <div className="text-[10px] text-gray-400 mt-1 font-mono">{sh.changed_by_name} ({sh.created_at})</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Immutable Version Snapshots</h4>
                <div className="space-y-2">
                  {versions.length === 0 ? (
                    <div className="text-xs text-gray-400 p-3 bg-gray-50 rounded-lg">Kayıtlı versiyon yok.</div>
                  ) : (
                    versions.map((v) => (
                      <div key={v.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs">
                        <div className="font-semibold text-gray-900">Version {v.version_number}: {v.title}</div>
                        <div className="text-gray-600 mt-0.5">{v.version_reason}</div>
                        <div className="text-[10px] text-gray-400 mt-1 font-mono">{v.created_by} ({v.created_at})</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};
